import { useCallback, useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { usePlatformElevationStore } from '../../store/platformElevationStore';
import { getOverview, runAction } from '../../lib/platformApi';
import { formatCountdown } from './RelativeTime';

/**
 * Always-visible warning that a platform admin currently holds elevated Admin
 * rights on someone else's organization.
 *
 * Mounted at the app root (next to ReportIssueButton) so it appears on every
 * route, including inside a live meeting. Amber rather than any `pp-*` token,
 * so it reads as ABNORMAL against the entire palette — this state should never
 * feel like part of the normal UI.
 */
export default function ElevatedBanner() {
  const { isPlatformAdmin, refreshMemberships } = useAuth();
  const { elevations, setElevations, removeElevation } = usePlatformElevationStore();
  const [busyId, setBusyId] = useState(null);
  const [, setTick] = useState(0);

  const hydrate = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      const data = await getOverview({ only: 'elevations' });
      const mine = (data?.activeElevations || []).filter(Boolean);
      setElevations(mine);
    } catch {
      /* silent — the banner is a safety net, not a feature */
    }
  }, [isPlatformAdmin, setElevations]);

  useEffect(() => {
    if (!isPlatformAdmin) {
      setElevations([]);
      return undefined;
    }
    void hydrate();
    const interval = setInterval(hydrate, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isPlatformAdmin, hydrate, setElevations]);

  // The banner is position:fixed so it survives every layout in the app, but
  // that would otherwise cover the sticky page headers (and their nav) exactly
  // when the user most needs to navigate out. Push the document down instead.
  useEffect(() => {
    if (!elevations.length) return undefined;
    const previous = document.body.style.paddingTop;
    document.body.style.paddingTop = `${elevations.length * 28}px`;
    return () => {
      document.body.style.paddingTop = previous;
    };
  }, [elevations.length]);

  // Re-render once a minute so the countdown stays honest.
  useEffect(() => {
    if (!elevations.length) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, [elevations.length]);

  const endElevation = async (elevation) => {
    setBusyId(elevation.organizationId);
    try {
      await runAction('revoke_self_org_admin', {
        targetId: elevation.organizationId,
        reason: 'Ended from banner',
      });
      removeElevation(elevation.organizationId);
      await refreshMemberships();
    } catch {
      await hydrate();
    } finally {
      setBusyId(null);
    }
  };

  if (!isPlatformAdmin || elevations.length === 0) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[95] border-b border-amber-300 bg-amber-50 text-amber-900 shadow-sm">
      {elevations.map((elevation) => {
        const remaining = formatCountdown(elevation.expiresAt);
        return (
          <div
            key={elevation.auditId}
            className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 text-xs sm:px-6"
            title={elevation.reason || undefined}
          >
            <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0">
              Platform elevation active in{' '}
              <strong className="font-semibold">
                {elevation.organizationName || elevation.organizationId}
              </strong>
              {remaining ? ` — expires in ${remaining}` : ' — expiring now'}
            </span>
            <button
              type="button"
              onClick={() => endElevation(elevation)}
              disabled={busyId === elevation.organizationId}
              className="ml-auto shrink-0 rounded-lg border border-amber-400 bg-white/60 px-2 py-0.5 font-semibold hover:bg-white disabled:opacity-50"
            >
              {busyId === elevation.organizationId ? 'Ending…' : 'End elevation'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
